import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';

export interface MultiSelectOption {
  value: string;
  label: string;
  meta?: string;
}

interface MultiSelectProps {
  label: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}

/**
 * Checkbox multi-select (native checkboxes — same pattern as the storage
 * collections source picker). Used for memory collections, skills, etc.
 */
export function MultiSelect({
  label,
  options,
  value,
  onChange,
  placeholder = 'No options available',
}: MultiSelectProps) {
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {options.length === 0 ? (
        <p className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
          {placeholder}
        </p>
      ) : (
        <div className="space-y-1.5 rounded-md border p-3">
          {options.map((opt) => {
            const checked = value.includes(opt.value);
            return (
              <label
                key={opt.value}
                className="flex cursor-pointer items-center gap-2 text-sm"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(opt.value)}
                  className="h-4 w-4 rounded border-input accent-primary"
                />
                <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                {opt.meta && <Badge variant="outline" className="shrink-0 text-xs">{opt.meta}</Badge>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}